import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { EntityTable } from '../_shared/components/EntityTable';
import { toneOf } from './readings';
const HEALTH = {
    late: {
        label: 'Late',
        variant: 'danger',
        rank: 0
    },
    at_risk: {
        label: 'At risk',
        variant: 'warning',
        rank: 1
    },
    on_track: {
        label: 'On track',
        variant: 'success',
        rank: 2
    },
    no_dates: {
        label: 'No dates',
        variant: 'neutral',
        rank: 3
    }
};
const TONE_TEXT = {
    success: 'text-success',
    danger: 'text-danger',
    neutral: ''
};
/** "Oct 21" and how far off it is ("today", "tomorrow", "in 6 days"). */ export function nextDue(iso, today) {
    if (!iso) return null;
    const d = new Date(`${iso}T00:00:00`);
    const t = new Date(`${today}T00:00:00`);
    const days = Math.round((d.getTime() - t.getTime()) / 86400000);
    const date = d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
    });
    const rel = days <= 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`;
    return {
        date,
        rel
    };
}
/** Worst first: late, then at risk, then on track, then no dates; most late first within. */ export function rankRows(rows) {
    return [
        ...rows
    ].sort((a, b)=>{
        const ra = HEALTH[a.s.health]?.rank ?? 9;
        const rb = HEALTH[b.s.health]?.rank ?? 9;
        if (ra !== rb) return ra - rb;
        const la = a.s.counts?.stateCounts.late ?? 0;
        const lb = b.s.counts?.stateCounts.late ?? 0;
        if (la !== lb) return lb - la;
        return a.s.name.localeCompare(b.s.name, undefined, {
            sensitivity: 'base'
        });
    });
}
/** Funded is shown only when some program in view has funding on record. */ export function hasFunding(rows) {
    return rows.some((r)=>(r.d?.currentTcv ?? 0) > 0);
}
function CellLink({ testid, onOpen, label, children }) {
    return /*#__PURE__*/ _jsx(SpecraButton, {
        "aria-label": label,
        "data-testid": testid,
        onClick: (e)=>{
            e.stopPropagation();
            onOpen();
        },
        size: "inline",
        variant: "link",
        children: children
    });
}
// specra-allow local-composite: the domain columns over EntityTable, which renders SpecraDataTable
export function ProgramsTable({ rows, today, isLoading, error, onRetry, emptyState }) {
    const navigate = useNavigate();
    const ranked = useMemo(()=>rankRows(rows), [
        rows
    ]);
    const funded = hasFunding(rows);
    const columns = useMemo(()=>{
        const cols = [
            {
                key: 'program',
                header: 'Program',
                render: (r)=>/*#__PURE__*/ _jsxs("div", {
                        className: "min-w-0",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                as: "div",
                                className: "font-medium",
                                size: "body-sm",
                                children: r.s.name || r.d?.programName || 'Untitled program'
                            }),
                            r.d?.contractNumber ? /*#__PURE__*/ _jsx(SpecraText, {
                                as: "div",
                                size: "meta",
                                tone: "muted",
                                children: r.d.contractNumber
                            }) : null
                        ]
                    })
            },
            {
                key: 'health',
                header: 'Health',
                headerInfo: 'Late: a deliverable is past due with nothing filed. At risk: one is due within 5 working days with no draft uploaded. On track: neither. No dates: nothing due in the period.',
                render: (r)=>{
                    const h = HEALTH[r.s.health] ?? HEALTH.no_dates;
                    return /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: h.variant,
                        children: h.label
                    });
                }
            },
            {
                key: 'owner',
                header: 'Owner',
                headerInfo: "The program's lead (its Program Lead seat, else its PM contact).",
                render: (r)=>r.s.ownerName ? /*#__PURE__*/ _jsx(SpecraText, {
                        size: "body-sm",
                        children: r.s.ownerName
                    }) : /*#__PURE__*/ _jsx(SpecraText, {
                        size: "body-sm",
                        tone: "muted",
                        children: "No lead"
                    })
            },
            {
                key: 'nextDue',
                header: 'Next due',
                align: 'right',
                render: (r)=>{
                    const n = nextDue(r.s.counts?.nextDueOn ?? '', today);
                    if (!n) {
                        return /*#__PURE__*/ _jsx(SpecraText, {
                            size: "body-sm",
                            tone: "muted",
                            children: "Nothing due"
                        });
                    }
                    return /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                as: "div",
                                size: "body-sm",
                                children: n.date
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                as: "div",
                                size: "meta",
                                tone: "muted",
                                children: n.rel
                            })
                        ]
                    });
                }
            },
            {
                key: 'onTime',
                header: 'On-time',
                align: 'right',
                headerInfo: 'Approved on or before the due date, out of everything that came due in the program’s current period (an overdue deliverable with nothing filed counts as a miss). Target: 100%.',
                render: (r)=>{
                    const k = r.s.onTime;
                    if (!k?.hasValue) {
                        return /*#__PURE__*/ _jsx(SpecraText, {
                            "data-testid": Testids.commandCenter.onTimeCell,
                            size: "body-sm",
                            tone: "muted",
                            children: "No dates due"
                        });
                    }
                    const tone = toneOf(k);
                    return /*#__PURE__*/ _jsxs("div", {
                        "data-kpi-tone": k.tone,
                        "data-testid": Testids.commandCenter.onTimeCell,
                        children: [
                            /*#__PURE__*/ _jsx(CellLink, {
                                label: `${r.s.name}: approved deliverables`,
                                onOpen: ()=>navigate(`/delivery/programs/${r.s.programId}/deliverables?state=approved`),
                                children: /*#__PURE__*/ _jsxs("span", {
                                    className: TONE_TEXT[tone],
                                    children: [
                                        k.value,
                                        "%"
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsxs(SpecraText, {
                                as: "div",
                                size: "meta",
                                tone: "muted",
                                children: [
                                    k.numerator,
                                    " of ",
                                    k.denominator
                                ]
                            })
                        ]
                    });
                }
            },
            {
                key: 'late',
                header: 'Late',
                align: 'right',
                render: (r)=>{
                    const late = r.s.counts?.stateCounts.late ?? 0;
                    if (late === 0) {
                        return /*#__PURE__*/ _jsx(SpecraText, {
                            "data-testid": Testids.cmPortfolio.lateCell,
                            size: "body-sm",
                            tone: "muted",
                            children: "0"
                        });
                    }
                    return /*#__PURE__*/ _jsx(CellLink, {
                        label: `${r.s.name}: late deliverables`,
                        onOpen: ()=>navigate(`/delivery/programs/${r.s.programId}/deliverables?state=late`),
                        testid: Testids.cmPortfolio.lateCell,
                        children: /*#__PURE__*/ _jsx("span", {
                            className: "text-danger",
                            children: late
                        })
                    });
                }
            }
        ];
        if (funded) {
            cols.push({
                key: 'funded',
                header: 'Funded',
                align: 'right',
                headerInfo: 'Funded amount as a share of the current contract value.',
                render: (r)=>(r.d?.currentTcv ?? 0) > 0 ? /*#__PURE__*/ _jsxs(SpecraText, {
                        size: "body-sm",
                        children: [
                            Math.floor(r.d?.fundedPercent ?? 0),
                            "%"
                        ]
                    }) : /*#__PURE__*/ _jsx(SpecraText, {
                        size: "body-sm",
                        tone: "muted",
                        children: "No data"
                    })
            });
        }
        return cols;
    }, [
        funded,
        navigate,
        today
    ]);
    return /*#__PURE__*/ _jsx(EntityTable, {
        columns: columns,
        data: ranked,
        emptyState: emptyState,
        error: error,
        getRowLabel: (r)=>r.s.name,
        isLoading: isLoading,
        onRetry: onRetry,
        onRowClick: (r)=>navigate(`/delivery/programs/${r.s.programId}`),
        rowKey: (r)=>r.s.programId,
        rowTestid: Testids.commandCenter.programRow,
        testid: Testids.commandCenter.programTable
    });
}
